<script setup lang="ts">
import { computed, ref } from 'vue';
import { useGlobalStore } from '@/stores/globalStore';
import { useRenderStore } from '@/builder/stores/renderStore';
import { AppUrls } from "@/constants/AppUrls";
import StandardButton from "@/components/shared/button/standard/Button.vue"
import { inject } from 'vue';
import { AxiosInstance } from 'axios';
import UserContext from '@/interfaces/account/UserContext';
import { ApiUrls } from '@/constants';
import { ModalDisplayType, PartialDisplayType } from '@/builder/enums';

const renderStore = useRenderStore();
const globalStore = useGlobalStore();

const axios = inject<AxiosInstance>("axios")!;

const props = defineProps({
    source: {
        type: String,
        required: true,
        default: "app"
    }
});

//data
const isLoading = ref(false);

//computed properties
const testModeEnabled = computed(() => {
    if (props.source === 'public') {
        return renderStore.isTestMode;
    }

    return globalStore.userContext?.isTestModeEnabled
});

const currentRoute = computed(() => {
    return globalStore.currentRoute;
});

const isTestDataRoute = computed(() => {

    if (currentRoute.value === null || currentRoute.value === undefined) {
        return false;
    }

    //if url is donor view
    if (currentRoute.value.includes(AppUrls.Donors.List + "/") && !currentRoute.value.includes('edit')) {
        return true;
    }

    //if url is project view
    if (currentRoute.value.includes(AppUrls.Projects.List + "/") && !currentRoute.value.includes('edit')) {
        return true;
    }

    //if url is segment view
    if (currentRoute.value.includes(AppUrls.Segments.List + "/") && !currentRoute.value.includes('edit')) {
        return true;
    }

    return currentRoute.value === "/" ||
        currentRoute.value === AppUrls.Dashboard ||
        currentRoute.value === AppUrls.Donors.List ||
        currentRoute.value === AppUrls.Gifts.List;
});

const testDataText = computed(() => {
    if (props.source === 'public') {
        return 'Test Data';
    }

    if (isTestDataRoute.value) {
        return 'Test Data Enabled';
    }

    return 'Test Data Disabled';
});

const warningText = computed(() => {
    if (props.source === 'public') {
        return 'Any transactions that go through this form will be added as <strong>test data</strong> to your RaiseDonors database.';
    }

    if (isTestDataRoute.value) {
        return 'The View Test Data toggle is on. Only donors and transactions created in Test Mode will be shown.';
    }

    return 'View Test Data is toggled on, but does not apply to this content. Changes you make here will impact your real data.';
});

const backgroundColor = computed(() => {
    if (props.source === 'public') {
        return '#EDF06D';
    }

    if (isTestDataRoute.value) {
        return '#EDF06D';
    }

    return '#F4F5B9';
});

const alertIcon = computed(() => {
    if (props.source === 'public') {
        return 'fa-solid fa-triangle-exclamation';
    }

    return 'ri-alert-line font-normal';
});

const getContainerClasses = computed(() => {
    if (props.source === 'public') {
        if (renderStore.page.partialDisplayType === PartialDisplayType.Modal &&
            renderStore.page.pageDesign.modalDisplay === ModalDisplayType.Lightbox) {
            return {
                'test-mode-banner-container': true,
                'modal-test-mode-banner': true,
            }
        }

        return {
            'test-mode-banner-container': true
        }
    }

    return {
        'test-mode-banner-container': true
    }
});

//methods
const openHowToUseArticle = () => {
    window.open('https://support.raisedonors.com/hc/en-us/articles/26535781932173-What-is-Test-Mode', '_blank');
}

const turnOffTestMode = () => {
    isLoading.value = true;

    axios.post<UserContext>(ApiUrls.Account.TurnOffTestMode)
        .then(response => {
            globalStore.setUserContext(response.data);

            if (isTestDataRoute.value) {
                //reload the page
                location.reload();
            }
        })
        .catch(error => {
            console.log(error);

            //try to get the user context from the server
            globalStore.loadUserContext();
        })
        .finally(() => {
            isLoading.value = false;
        });
}
</script>

<template>
    <section v-if="testModeEnabled" :class="getContainerClasses">

        <div class="test-mode-banner-top w-full"></div>

        <div class="test-mode-banner-content items-center flex justify-center w-full">

            <div class="test-mode-text items-center flex flex-row justify-center w-full">
                <span class="warning-icon items-center text-primary-green-visited flex">
                    <i :class="alertIcon" class="text-xl leading-none"></i>
                    <span class="block text-xs font-semibold leading-none ml-1 uppercase">{{ testDataText }}</span>
                </span>

                <p class="text-primary-green-visited text-xs mb-0 ml-4" v-html="warningText"></p>
            </div>

            <div class="test-mode-banner-buttons items-center flex flex-row justify-end w-1/5">
                <StandardButton class="how-to-use-button font-normal mr-2" text="How to Use" color="neutral"
                    size="small" :hasTextAndIcon="true" leftIcon="ri-question-line" @click="openHowToUseArticle" />

                <StandardButton v-if="source === 'app'" class="turn-off-button font-normal" color="neutral" size="small"
                    :text="isLoading ? 'Turning Off...' : 'Turn Off'" :processing="isLoading"
                    @click="turnOffTestMode" />
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.test-mode-banner-container {
    background-color: v-bind(backgroundColor);
    height: auto;
    text-align: center;
    width: 100%;
}

.test-mode-banner-top {
    height: 3px;
    background-image: linear-gradient(to right, #253C47 50%, #24374633 50%);
    background-size: 2.5rem 100%;
}

.test-mode-banner-content {
    padding: .625rem 2.5rem;
}

.how-to-use-button,
.turn-off-button {
    background-color: transparent;
    border-color: rgb(37 60 71 / 1);
    color: rgb(37 60 71 / 1);
    height: 1.5rem;
    padding-left: .6875rem;
    padding-right: .6875rem;
}

.turn-off-button {
    background-color: rgb(37 60 71 / 1);
    color: rgb(255 255 255 / 1);
    padding-bottom: .25rem;
    padding-top: .25rem;
}
</style>
